import { Link, NavLink, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Sparkles, Home, ListChecks, Droplets, Coins, TrendingUp, LogOut, Menu, KeyRound } from "lucide-react";
import { useState } from "react";
import ChildSelector from "@/components/ChildSelector";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";
import NotificationBell from "@/components/NotificationBell";

const PARENT_NAV = [
    { to: "/", label: "Hoje", icon: Home, testid: "nav-home" },
    { to: "/tarefas", label: "Tarefas", icon: ListChecks, testid: "nav-tasks" },
    { to: "/banho", label: "Banho", icon: Droplets, testid: "nav-bath" },
    { to: "/pagamentos", label: "Pagamentos", icon: Coins, testid: "nav-payments" },
    { to: "/desempenho", label: "Desempenho", icon: TrendingUp, testid: "nav-performance" },
];

const CHILD_NAV = [
    { to: "/", label: "Hoje", icon: Home, testid: "nav-home" },
    { to: "/tarefas", label: "Tarefas", icon: ListChecks, testid: "nav-tasks" },
    { to: "/banho", label: "Banho", icon: Droplets, testid: "nav-bath" },
    { to: "/desempenho", label: "Minha semana", icon: TrendingUp, testid: "nav-performance" },
];

export default function Layout({ children }) {
    const { user, logout, isChild } = useAuth();
    const { children: kids } = useApp();
    const [open, setOpen] = useState(false);
    const [pwdOpen, setPwdOpen] = useState(false);
    const loc = useLocation();
    const NAV = isChild ? CHILD_NAV : PARENT_NAV;

    return (
        <div className="App min-h-screen grain">
            <header className="sticky top-0 z-30 border-b border-[#E2DCD5] bg-[#FAF8F5]/80 backdrop-blur-xl">
                <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4">
                    <Link to="/" className="flex items-center gap-2.5" data-testid="brand-link">
                        <div className="grid h-9 w-9 place-items-center rounded-xl bg-[#D96B43] text-white shadow-sm">
                            <Sparkles className="h-5 w-5" strokeWidth={2.4} />
                        </div>
                        <div className="leading-tight">
                            <div className="font-bold text-slate-900" style={{ fontFamily: "Outfit" }}>RotinaKids</div>
                            <div className="text-[11px] uppercase tracking-widest text-[#8a7968]">
                                {isChild ? "modo criança" : "gestão familiar"}
                            </div>
                        </div>
                    </Link>
                    <div className="hidden md:flex items-center gap-3">
                        {!isChild && kids.length > 0 && <ChildSelector />}
                        <div className="text-sm text-slate-600 pl-2 border-l border-[#E2DCD5]">
                            <span className="font-medium">{isChild ? "Oi, " : ""}{user?.name}</span>
                        </div>
                        <NotificationBell />
                        {!isChild && (
                            <Button variant="ghost" size="icon" onClick={() => setPwdOpen(true)} data-testid="change-password-button" className="rounded-full" title="Trocar senha">
                                <KeyRound className="h-4 w-4" />
                            </Button>
                        )}
                        <Button variant="ghost" size="icon" onClick={logout} data-testid="logout-button" className="rounded-full" title="Sair">
                            <LogOut className="h-4 w-4" />
                        </Button>
                    </div>
                    <button className="md:hidden rounded-lg border border-[#E2DCD5] p-2" onClick={() => setOpen((v) => !v)} data-testid="mobile-menu-toggle">
                        <Menu className="h-5 w-5" />
                    </button>
                </div>
                {open && (
                    <div className="md:hidden border-t border-[#E2DCD5] px-5 py-3 space-y-3">
                        {!isChild && kids.length > 0 && <ChildSelector />}
                        <div className="flex items-center justify-between rounded-full border border-[#E2DCD5] px-4 py-2">
                            <span className="text-sm text-slate-600">Notificações</span>
                            <NotificationBell />
                        </div>
                        {!isChild && (
                            <Button variant="outline" onClick={() => { setOpen(false); setPwdOpen(true); }} data-testid="change-password-button-mobile" className="w-full justify-start rounded-full">
                                <KeyRound className="h-4 w-4 mr-2" /> Trocar senha
                            </Button>
                        )}
                        <Button variant="outline" onClick={() => { setOpen(false); logout(); }} data-testid="logout-button-mobile" className="w-full justify-start rounded-full">
                            <LogOut className="h-4 w-4 mr-2" /> Sair ({user?.name})
                        </Button>
                    </div>
                )}
            </header>

            <main className="mx-auto max-w-6xl px-5 pt-6 pb-28 md:pb-10 md:pl-24">
                {children}
            </main>

            <nav className="md:hidden fixed bottom-0 inset-x-0 z-30 border-t border-[#E2DCD5] bg-white/95 backdrop-blur-xl">
                <ul className={`grid ${isChild ? "grid-cols-4" : "grid-cols-5"}`}>
                    {NAV.map((n) => {
                        const Icon = n.icon;
                        const active = loc.pathname === n.to;
                        return (
                            <li key={n.to}>
                                <NavLink to={n.to} data-testid={n.testid + "-mobile"} className={`flex flex-col items-center justify-center gap-1 py-2.5 text-[10px] font-medium ${active ? "text-[#D96B43]" : "text-slate-500"}`}>
                                    <Icon className="h-5 w-5" strokeWidth={active ? 2.4 : 2} />
                                    {n.label}
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            <nav className="hidden md:block fixed left-5 top-28 w-16">
                <ul className="space-y-2 rounded-2xl border border-[#E2DCD5] bg-white p-2 shadow-sm">
                    {NAV.map((n) => {
                        const Icon = n.icon;
                        const active = loc.pathname === n.to;
                        return (
                            <li key={n.to}>
                                <NavLink to={n.to} data-testid={n.testid} className={`grid h-12 place-items-center rounded-xl ${active ? "bg-[#D96B43] text-white" : "text-slate-500 hover:bg-[#F3EFEA]"}`} title={n.label}>
                                    <Icon className="h-5 w-5" />
                                </NavLink>
                            </li>
                        );
                    })}
                </ul>
            </nav>

            {!isChild && <ChangePasswordDialog open={pwdOpen} onOpenChange={setPwdOpen} />}
        </div>
    );
}
